import { MarkHeading } from '../../../components/MarkControls';
import * as layout from '../../../components/markLayout.module.css';
import {
    MarkChoiceLabel,
    MarkChoice,
    MarkInput,
    MarkTimeInput,
    MarkLabel
} from '../../../components/MarkControls';
import React, { useId, useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import { getScheduleError, getScheduleMinimum, getLocalSendTimeError } from '../blastFormUtils';
import SegmentPicker from '../../Segments/SegmentPicker';
import type { PickedSegment } from '../../Segments/SegmentPicker';
import type { BlastAccountRecipients, BlastScheduleMode } from '../../../types';
type Step2Data = {
    segmentId: number | null;
    segmentName: string | null;
    accountRecipients: BlastAccountRecipients;
    fromAddress: string;
    scheduleMode: BlastScheduleMode;
    scheduledAt: string;
    localSendTime: string;
};
type Props = {
    data: Step2Data;
    onChange: (data: Step2Data) => void;
    deliveryScope: string;
    errors: string[];
    showErrors?: boolean;
};
const Step2Audience: React.FC<Props> = ({
    data,
    onChange,
    deliveryScope,
    errors,
    showErrors = errors.length > 0
}) => {
    const fromId = useId();
    const scheduleId = useId();
    const localTimeId = useId();
    const [minimum, setMinimum] = useState(getScheduleMinimum);
    const [scheduleTouched, setScheduleTouched] = useState(false);
    const [localTimeTouched, setLocalTimeTouched] = useState(false);
    const scheduleError =
        data.scheduleMode === 'scheduled' ? getScheduleError(data.scheduledAt) : null;
    const localTimeError =
        data.scheduleMode === 'scheduled' ? getLocalSendTimeError(data.localSendTime) : null;
    const visibleScheduleError = scheduleTouched || showErrors ? scheduleError : null;
    const visibleLocalTimeError = localTimeTouched || showErrors ? localTimeError : null;
    const otherErrors = errors.filter(
        (error) => error !== scheduleError && error !== localTimeError
    );
    const handleSegmentChange = (picked: PickedSegment | null) => {
        onChange({
            ...data,
            segmentId: picked?.segment_id ?? null,
            segmentName: picked?.name ?? null
        });
    };
    return (
        <div className={layout.form}>
            <SegmentPicker value={data.segmentId} onChange={handleSegmentChange} />
            {deliveryScope === 'PER_ACCOUNT' && (
                <fieldset aria-label="Account recipients" className={layout.fieldset}>
                    <MarkHeading role="h6">Account recipients</MarkHeading>
                    <div className={layout.choiceGroup}>
                        <MarkChoiceLabel>
                            <MarkChoice
                                type="radio"
                                name="accountRecipients"
                                value="ALL"
                                checked={data.accountRecipients === 'ALL'}
                                onChange={() => onChange({ ...data, accountRecipients: 'ALL' })}
                            />
                            All account members
                        </MarkChoiceLabel>
                        <MarkChoiceLabel>
                            <MarkChoice
                                type="radio"
                                name="accountRecipients"
                                value="ADMIN_ONLY"
                                checked={data.accountRecipients === 'ADMIN_ONLY'}
                                onChange={() =>
                                    onChange({ ...data, accountRecipients: 'ADMIN_ONLY' })
                                }
                            />
                            Admin only
                        </MarkChoiceLabel>
                    </div>
                </fieldset>
            )}
            <div className={layout.field}>
                <MarkLabel htmlFor={fromId}>From address (optional)</MarkLabel>
                <MarkInput
                    id={fromId}
                    type="email"
                    value={data.fromAddress}
                    onChange={(e) => onChange({ ...data, fromAddress: e.currentTarget.value })}
                    placeholder="noreply@example.com"
                />
            </div>
            <fieldset aria-label="Schedule" className={layout.fieldset}>
                <MarkHeading role="h6">Schedule</MarkHeading>
                <div className={layout.choiceGroup}>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="scheduleMode"
                            value="immediate"
                            checked={data.scheduleMode === 'immediate'}
                            onChange={() => onChange({ ...data, scheduleMode: 'immediate' })}
                        />
                        Send immediately after publish
                    </MarkChoiceLabel>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="scheduleMode"
                            value="scheduled"
                            checked={data.scheduleMode === 'scheduled'}
                            onChange={() => onChange({ ...data, scheduleMode: 'scheduled' })}
                        />
                        Plan a later send
                    </MarkChoiceLabel>
                </div>
            </fieldset>
            {data.scheduleMode === 'scheduled' && (
                <div
                    style={{
                        marginTop: '1rem',
                        flexWrap: 'wrap'
                    }}
                    className={layout.fieldGrid}
                >
                    <div className={layout.field}>
                        <MarkLabel htmlFor={scheduleId}>Send at (UTC)</MarkLabel>
                        <MarkInput
                            id={scheduleId}
                            data-field="scheduled-at"
                            type="datetime-local"
                            value={data.scheduledAt}
                            min={minimum}
                            onFocus={() => setMinimum(getScheduleMinimum())}
                            onBlur={() => setScheduleTouched(true)}
                            aria-invalid={!!visibleScheduleError}
                            aria-describedby={
                                visibleScheduleError ? `${scheduleId}-error` : undefined
                            }
                            onChange={(e) =>
                                onChange({ ...data, scheduledAt: e.currentTarget.value })
                            }
                        />
                        {visibleScheduleError && (
                            <p
                                id={`${scheduleId}-error`}
                                role="alert"
                                className={layout.fieldError}
                            >
                                {visibleScheduleError}
                            </p>
                        )}
                    </div>
                    <div className={layout.field}>
                        <MarkLabel htmlFor={localTimeId}>
                            Local send time (optional, HH:MM)
                        </MarkLabel>
                        <MarkTimeInput
                            id={localTimeId}
                            data-field="local-send-time"
                            onBlur={() => setLocalTimeTouched(true)}
                            aria-invalid={!!visibleLocalTimeError}
                            aria-describedby={
                                visibleLocalTimeError ? `${localTimeId}-error` : undefined
                            }
                            value={data.localSendTime}
                            onChange={(e) =>
                                onChange({ ...data, localSendTime: e.currentTarget.value })
                            }
                        />
                        {visibleLocalTimeError && (
                            <p
                                id={`${localTimeId}-error`}
                                role="alert"
                                className={layout.fieldError}
                            >
                                {visibleLocalTimeError}
                            </p>
                        )}
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)',
                                marginTop: '0.2rem',
                                maxWidth: '24rem'
                            }}
                        >
                            Recipient receives at this wall-clock time in their timezone. Never
                            before the UTC time above. Missed same-day sends go out the next day.
                        </P>
                    </div>
                </div>
            )}
            {otherErrors.length > 0 && (
                <div role="alert" className={layout.notice}>
                    {otherErrors.map((err) => (
                        <P
                            key={err}
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)'
                            }}
                        >
                            {err}
                        </P>
                    ))}
                </div>
            )}
        </div>
    );
};
export type { Step2Data };
export default Step2Audience;
